import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/dash/Common";
import { PaymentBadge, Pill } from "@/components/Badges";
import StudentCard from "@/components/StudentCard";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { daysLeft, fmtDate, fmtDateTime, isPackageActive, rupiah } from "@/lib/format";

const PROFILE_FIELDS = [["name", "Nama lengkap"], ["phone", "No. HP"], ["birth_date", "Tanggal lahir", "date"], ["school", "Asal sekolah / instansi"], ["address", "Alamat"]];

function ProfileForm() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState(Object.fromEntries(PROFILE_FIELDS.map(([k]) => [k, user[k] || ""])));
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data);
      toast.success("Profil diperbarui");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="grid sm:grid-cols-2 gap-5 max-w-2xl" data-testid="profile-form">
      <div className="space-y-2 sm:col-span-2"><Label>Email</Label><Input value={user.email} disabled /></div>
      {PROFILE_FIELDS.map(([k, l, t]) => (
        <div key={k} className="space-y-2"><Label>{l}</Label><Input type={t || "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`profile-${k.replace("_", "-")}-input`} /></div>
      ))}
      <div className="sm:col-span-2"><Button type="submit" disabled={busy} className="bg-slate-900" data-testid="profile-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan Profil</Button></div>
    </form>
  );
}

export function PasswordForm() {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", form);
      toast.success("Kata sandi diperbarui");
      setForm({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={save} className="space-y-4 max-w-sm" data-testid="change-password-form">
      <div className="space-y-2"><Label>Kata sandi lama</Label><Input type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="current-password-input" /></div>
      <div className="space-y-2"><Label>Kata sandi baru</Label><Input type="password" required value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="new-password-input" /></div>
      <Button type="submit" className="bg-slate-900" data-testid="change-password-button">Ubah Kata Sandi</Button>
    </form>
  );
}

function PackagePanel() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { data: packages } = useApi("/packages");
  const { data: payments } = useApi("/student/payments");
  const [pkgId, setPkgId] = useState("");
  const active = isPackageActive(user);
  const current = (packages || []).find((p) => p.id === user.package_id);
  const order = async () => {
    try {
      const { data } = await api.post("/student/orders", { package_id: pkgId });
      toast.success(`Tagihan ${data.invoice} dibuat`);
      nav(`/pembayaran?invoice=${data.invoice}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-8">
      <div className="rounded-lg bg-slate-900 text-white p-6 flex flex-wrap justify-between gap-4" data-testid="active-package-card">
        <div>
          <p className="text-xs uppercase tracking-widest text-amber-400">Paket aktif</p>
          <p className="font-display text-2xl font-bold mt-2">{current?.name || "Belum ada paket"}</p>
          <p className="text-sm text-slate-400 mt-1">{active ? `Berlaku s.d. ${fmtDate(user.package_expires_at)} (${daysLeft(user.package_expires_at)} hari lagi)` : user.package_expires_at ? `Kedaluwarsa ${fmtDate(user.package_expires_at)}` : "Selesaikan pembayaran untuk aktivasi"}</p>
        </div>
        <div className="self-start"><Pill tone={active ? "green" : "red"}>{active ? "Aktif" : "Tidak aktif"}</Pill></div>
      </div>
      <StudentCard user={user} pkgName={current?.name} />
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="font-display font-semibold mb-3">Beli / perpanjang paket</p>
        <div className="flex flex-wrap gap-3">
          <select className="h-9 rounded-md border border-input bg-white px-3 text-sm min-w-64" value={pkgId} onChange={(e) => setPkgId(e.target.value)} data-testid="order-package-select">
            <option value="">— Pilih paket —</option>
            {(packages || []).map((p) => <option key={p.id} value={p.id}>{p.name} · {rupiah(p.price)}</option>)}
          </select>
          <Button disabled={!pkgId} onClick={order} className="bg-amber-500 text-slate-900 hover:bg-amber-400" data-testid="order-create-invoice-button">Buat Tagihan</Button>
        </div>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100" data-testid="payment-history">
        <p className="font-display font-semibold p-6 pb-3">Riwayat pembayaran</p>
        {(payments || []).length === 0 && <p className="px-6 pb-6 text-sm text-slate-500">Belum ada transaksi.</p>}
        {(payments || []).map((p) => (
          <div key={p.id} className="px-6 py-4 flex flex-wrap justify-between items-center gap-3">
            <div><p className="font-mono text-sm font-semibold">{p.invoice}</p><p className="text-xs text-slate-500">{p.package_name} · {fmtDateTime(p.created_at)}</p></div>
            <div className="flex items-center gap-3"><span className="font-semibold text-sm">{rupiah(p.amount)}</span><PaymentBadge status={p.status} /><Link to={`/pembayaran?invoice=${p.invoice}`} className="text-xs text-amber-600 hover:underline" data-testid={`payment-detail-${p.invoice}`}>Detail</Link></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StudentProfile() {
  return (
    <div data-testid="student-profile-page">
      <PageHeader eyebrow="Akun" title="Profil, paket & masa berlaku" />
      <Tabs defaultValue="paket">
        <TabsList>
          <TabsTrigger value="paket" data-testid="tab-package">Paket & Pembayaran</TabsTrigger>
          <TabsTrigger value="profil" data-testid="tab-profile">Profil</TabsTrigger>
          <TabsTrigger value="keamanan" data-testid="tab-security">Keamanan</TabsTrigger>
        </TabsList>
        <TabsContent value="paket" className="mt-6"><PackagePanel /></TabsContent>
        <TabsContent value="profil" className="mt-6"><ProfileForm /></TabsContent>
        <TabsContent value="keamanan" className="mt-6"><PasswordForm /></TabsContent>
      </Tabs>
    </div>
  );
}
